import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Award, ChevronRight, Pencil, Plus, StickyNote, User, Users } from "lucide-react";
import SidebarNavIcon from "../ui/SidebarNavIcon";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import EditNoteModal from "./EditNoteModal";
import {
  formatSocialCount,
  formatUserLevel,
  PROFILE_SECTIONS,
  truncateNote,
} from "../../utils/profileHelpers";
import { PROFILE_SECTION_ICONS } from "../../utils/sidebarNavIcons";

const ProfileIdentityRail = ({ user, userId, activeSection, onSectionSelect, onNotify }) => {
  const [noteOpen, setNoteOpen] = useState(false);

  if (!user) return null;

  const hasNote = Boolean(String(user.notes || "").trim());
  const notePreview = truncateNote(user.notes, 80) || "Add a short personal note…";
  const followers = formatSocialCount(user.followers);
  const following = formatSocialCount(user.following);

  return (
    <>
      <aside className="prof-identity-rail" aria-label="Profile summary">
        <div className="prof-identity-card">
          <div className="prof-identity-top">
            <img
              className="prof-avatar"
              src={generateAvatarUrl(user.name)}
              alt=""
              width={56}
              height={56}
            />
            <div className="prof-identity-copy">
              <p className="prof-kicker">Member</p>
              <h1>{user.name || "Member"}</h1>
              <div className="prof-identity-pills">
                <span className="prof-level-pill">
                  <Award size={10} aria-hidden />
                  {formatUserLevel(user.userLevel)}
                </span>
                <span className="prof-live-pill">
                  <span className="prof-live-dot" aria-hidden />
                  Live
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            className={`prof-note-well prof-note-well-btn${hasNote ? "" : " prof-note-well-empty"}`}
            onClick={() => setNoteOpen(true)}
          >
            <StickyNote size={12} aria-hidden />
            <p>{notePreview}</p>
            <span className="prof-note-action">
              {hasNote ? <Pencil size={10} aria-hidden /> : <Plus size={10} aria-hidden />}
              {hasNote ? "Edit" : "Add"}
            </span>
          </button>

          <div className="prof-social-strip">
            <Link
              to="/app/profile/connections/followers"
              className="prof-social-tile"
              aria-label={`View ${followers} followers`}
            >
              <span className="prof-social-icon" aria-hidden>
                <Users size={12} />
              </span>
              <span className="prof-social-copy">
                <span className="prof-social-value tabular-nums">{followers}</span>
                <span className="prof-social-label">Followers</span>
              </span>
              <ChevronRight size={13} strokeWidth={2.4} className="prof-social-chevron" aria-hidden />
            </Link>
            <Link
              to="/app/profile/connections/following"
              className="prof-social-tile"
              aria-label={`View ${following} following`}
            >
              <span className="prof-social-icon" aria-hidden>
                <User size={12} />
              </span>
              <span className="prof-social-copy">
                <span className="prof-social-value tabular-nums">{following}</span>
                <span className="prof-social-label">Following</span>
              </span>
              <ChevronRight size={13} strokeWidth={2.4} className="prof-social-chevron" aria-hidden />
            </Link>
          </div>
        </div>

        <nav className="prof-section-nav" aria-label="Profile sections">
          {PROFILE_SECTIONS.map((section) => {
            const active = activeSection === section.id;
            return (
              <button
                key={section.id}
                type="button"
                className={`prof-section-nav-btn ${active ? "prof-section-nav-btn-active" : ""}`}
                onClick={() => onSectionSelect(section.id)}
                aria-current={active ? "page" : undefined}
              >
                <span className="prof-section-nav-icon">
                  <SidebarNavIcon
                    icon={PROFILE_SECTION_ICONS[section.id]}
                    size={14}
                    className="prof-section-nav-icon-glyph"
                  />
                </span>
                <span className="prof-section-nav-copy">
                  <em>{section.eyebrow}</em>
                  <strong>{section.label}</strong>
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      <EditNoteModal
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        userId={userId}
        currentNote={user.notes}
        onNotify={onNotify}
      />
    </>
  );
};

export default ProfileIdentityRail;
